import { memo, useEffect, useState } from "react";

import { Button } from "@mui/material";

import { useMobileVerificationContext } from "@zenith/context";
import { AppNavigation } from "@zenith/hooks";
import { PinInputComponent } from "@zenith/components";
import {
  ApiResponseDTOEncryptionDTO,
  OnboardingApiService,
  UtilityApiService,
} from "@zenith/services/user";
import {
  DEFAULT_CHANNEL,
  TRANSACTIONS_PIN_LENGTH,
} from "@zenith/data/constants";
import {
  setConfirmTransactionPinValidations,
  setTransactionPinValidations,
} from "@zenith/features/Authentication/validations/AuthValidations";
import { useNavigationType } from "react-router-dom";
import { ApiResponseDTO } from "@zenith/services/utils";
import { ACCOUNT_CREATED, SIGN_UP } from "@zenith/routes/paths";
import { encrypt } from "@zenith/utils";

/* eslint-disable-next-line */
export interface SignUpTransactionsPinPageProps {}

const SignUpTransactionsPinPage = memo(function SignUpTransactionsPinPage(
  props: SignUpTransactionsPinPageProps
) {
  const xApiKey = import.meta.env.VITE_X_API_KEY;

  const { navigateTo } = AppNavigation();
  const navType = useNavigationType();
  const {
    mobileVerificationContext: { countryCode, phoneNumber },
    setFirstName,
  } = useMobileVerificationContext();

  const [formData, setFormData] = useState<{
    pin: string;
    confirmPin: string;
  }>({
    pin: "",
    confirmPin: "",
  });

  useEffect(() => {
    if (navType === "POP") {
      navigateTo(SIGN_UP);
    }
  }, [navType, navigateTo]);

  const [newPin, setNewPin] = useState<Array<number | string>>(
    new Array(TRANSACTIONS_PIN_LENGTH).fill("")
  );
  const [newConfirmPin, setConfirmNewPin] = useState<Array<number | string>>(
    new Array(TRANSACTIONS_PIN_LENGTH).fill("")
  );

  const [pinError, setPinError] = useState<{
    hasError: boolean;
    message: string;
  }>({
    hasError: false,
    message: "",
  });

  const [confirmPinError, setConfirmPinError] = useState<{
    hasError: boolean;
    message: string;
  }>({
    hasError: false,
    message: "",
  });

  const onPinChanged = (pinEntry: number | string, index: number) => {
    const pin = [...newPin];
    pin[index] = pinEntry;
    setNewPin(pin);
    setPinError({ message: "", hasError: false });
    setFormData({ ...formData, pin: pin.join("") });
  };

  const onPinBlur = (): void => {
    setPinError(setTransactionPinValidations(formData.pin));
  };

  const onConfirmPinChanged = (pinEntry: number | string, index: number) => {
    const confirmPin = [...newConfirmPin];
    confirmPin[index] = pinEntry;
    setConfirmPinError({ message: "", hasError: false });
    setConfirmNewPin(confirmPin);
    setFormData({ ...formData, confirmPin: confirmPin.join("") });
  };

  const onConfirmPinBlur = (): void => {
    setConfirmPinError(
      setConfirmTransactionPinValidations(formData.confirmPin)
    );
  };

  //#region Aux Methods

  const onBackButtonClick = (): void => {
    navigateTo(SIGN_UP);
  };

  const mapCountryCodeToEnum = () => {
    switch (countryCode) {
      case "NG":
        return "NG";
      case "GH":
        return "GH";
      default:
        return "NG";
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (formData.pin !== formData.confirmPin) {
      setConfirmPinError({
        hasError: true,
        message:
          "PIN code doesn´t match. Please make sure to enter the same PIN in both fields",
      });
      return;
    }

    UtilityApiService.getPublicKey(DEFAULT_CHANNEL, xApiKey)
      .then((response: ApiResponseDTOEncryptionDTO) => {
        if (!response.data) return;
        if (response.success) {
          const publicKeyBase64 = response.data.publicKeyBase64;

          const encryptedPin = encrypt(formData.pin, publicKeyBase64 || "");
          const confirmEncryptedPin = encrypt(
            formData.confirmPin,
            publicKeyBase64 || ""
          );

          if (
            publicKeyBase64 &&
            publicKeyBase64.length > 0 &&
            encryptedPin &&
            encryptedPin.length > 0 &&
            confirmEncryptedPin &&
            confirmEncryptedPin.length > 0
          ) {
            validatePin(encryptedPin, confirmEncryptedPin);
          }
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  };

  function validatePin(
    encryptedPin: string,
    confirmEncryptedPin: string
  ): void {
    // const request: OnboardUserRequestDTO = {
    //   confirmTransactionPin: confirmEncryptedPin,
    //   countryCode: mapCountryCodeToEnum(),
    //   transactionPin: encryptedPin,
    // };
    // OnboardingApiService.onboardUser(phoneNumber, request)
    //   .then((response: OnboardUserResponseDTO | ApiResponseDTO) => {
    //     if ("data" in response && response.data) {
    //       const firstName = response.data.firstName;
    //       if (firstName) {
    //         setFirstName(firstName);
    //       } else {
    //         setFirstName("James");
    //       }
    //       navigateTo(ACCOUNT_CREATED);
    //     }
    //   })
    //   .catch((error) => {
    //     if (error.body.internalCode === "ZENZ-CROSS-008") {
    //       setConfirmPinError({
    //         hasError: true,
    //         message: "User was not found by phone number",
    //       });
    //     }
    //   });
  }

  //#endregion Aux Methods

  return (
    <form
      className="mt-248 flex w-full grow justify-start"
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col md:mx-36">
        <div className="flex flex-col">
          <h5 className="text-4xl">Transaction PIN</h5>
          <span className="mt-[16px] text-neutral-600">
            This will be used to authorise your transactions
          </span>
        </div>
        <div className="mt-12">
          <div className="mb-10 flex w-full flex-row space-x-1">
            <PinInputComponent
              id="transactions-pin"
              label="Create four (4) digit PIN"
              labelClassName="mb-1 text-neutral-600"
              pinLength={TRANSACTIONS_PIN_LENGTH}
              pin={newPin}
              onPinChanged={onPinChanged}
              pinContainerClass="gap-2 md:gap-4 xl:gap-6"
              onBlur={() => {
                onPinBlur();
              }}
              hasError={pinError?.hasError}
              type="tel"
              isValidating={false}
              validationMessage={pinError?.message}
            />
          </div>

          <div className="mt-12 flex w-full flex-row space-x-1">
            <PinInputComponent
              id="transactions-confirm-pin"
              label="Confirm four (4) digit PIN"
              labelClassName="mb-1 text-neutral-600"
              pinLength={TRANSACTIONS_PIN_LENGTH}
              pinContainerClass="gap-2 md:gap-4 xl:gap-6"
              pin={newConfirmPin}
              onPinChanged={onConfirmPinChanged}
              onBlur={() => {
                onConfirmPinBlur();
              }}
              hasError={confirmPinError?.hasError}
              type="tel"
              masked={true}
              isValidating={false}
              validationMessage={confirmPinError?.message}
            />
          </div>

          <div className="mt-20 flex flex-row items-center justify-center gap-4">
            <Button
              id="go-back-to-sign-up"
              className="outline-solid w-1/2 rounded-lg bg-white px-6 py-3 normal-case text-neutral-500 outline outline-1 hover:bg-gray-300 disabled:bg-neutral-200"
              onClick={onBackButtonClick}
            >
              Back
            </Button>

            <Button
              id="continue-to-account-created"
              className="w-1/2 rounded-lg bg-primary-300 px-6 py-3 normal-case text-white hover:bg-gray-300 disabled:bg-neutral-200"
              size="large"
              type="submit"
              disabled={
                formData.pin.length < TRANSACTIONS_PIN_LENGTH ||
                formData.confirmPin.length < TRANSACTIONS_PIN_LENGTH
              }
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
});

export default SignUpTransactionsPinPage;
